import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { Flex, useDisclosure } from '@chakra-ui/react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';
import type { PageFC } from '@salesforce/pwa-kit-react-sdk';
import { useProducts } from '../../bff-api/products/useProducts';
import { EnhancedError } from '../../components/_error/EnhancedError';
import { Layout } from '../../components/layout/Layout';
import { PdpProvider } from '../../contexts/pdp/usePDP';
import { RecaptchaAction } from '../../recaptcha/recaptcha';
import { breakpoints } from '../../theme/foundations/breakpoints';
import { Breadcrumb, Header } from '../../ui-components';
import { useBreadcrumbs } from '../../ui-components/breadcrumbs/useBreadcrumbs';
import { GiftCardsSchema } from '../../utils/schema/GiftCardsSchema';
import { GiftCard } from './components/GiftCard';
import { GiftCardsFAQSection } from './components/GiftCardsFAQSection/GiftCardsFAQSection';
import {
    GiftCardsBreadcrumbsSkeleton,
    GiftCardsCardContainerSkeleton,
    GiftCardsFAQCardSkeleton,
    GiftCardsTitleSkeleton,
    GiftCardsYouGiveCardSkeleton,
} from './components/GiftCardsSkeleton';
import { GiftCardsYouGiveSection } from './components/GiftCardsYouGiveSection';
import { CheckBalanceModal } from './components/check-balance-modal';
import { convertFAQItemsToSchemaItems, getFAQItems } from './utils/faq';
import { getRootCategory } from './utils/getRootCategory';

const pgcSku = 'GCP-15269';
const egcSku = 'GCP-15271';

export const GiftCardsLayout = (): ReactElement | null => {
    const { isOpen, onOpen, onClose } = useDisclosure();

    const { data, isFetching: isLoading } = useProducts([pgcSku, egcSku]);
    const { physicalCardProduct } = useMemo(
        () => ({
            physicalCardProduct: data?.find((product) => product.id === pgcSku),
        }),
        [data],
    );

    const intl = useIntl();
    const breadcrumbs = useBreadcrumbs(
        getRootCategory(physicalCardProduct?.c_primaryCategory?.parentCategoryTree),
    );

    const faqItems = getFAQItems(intl, onOpen);
    const faqSchemaItems = convertFAQItemsToSchemaItems(faqItems);

    return (
        <Layout>
            <GiftCardsSchema breadcrumbTrail={breadcrumbs} faqItems={faqSchemaItems} />
            <Flex
                direction="column"
                gap={{ base: 8 }}
                paddingBottom={{ base: 8, lg: 16 }}
                paddingX={{ md: 8, lg: 10, xl: 16 }}
                data-testid="gift-cards-layout"
                maxWidth={breakpoints.xl}
                marginX="auto"
            >
                <CheckBalanceModal
                    id="check-balance-modal"
                    isOpen={isOpen}
                    onClose={onClose}
                    action={RecaptchaAction.GIFT_CARD_BALANCE_LANDING_PAGE}
                />
                <Flex direction="column">
                    <GiftCardsBreadcrumbsSkeleton isLoaded={!isLoading}>
                        <Breadcrumb itemsList={breadcrumbs} />
                    </GiftCardsBreadcrumbsSkeleton>
                    <GiftCardsTitleSkeleton isLoaded={!isLoading}>
                        <Header as="h2" variant="header2" style="bold" color="slt-pepper.1000">
                            <FormattedMessage id="gift_cards.title" defaultMessage="Gift Cards" />
                        </Header>
                    </GiftCardsTitleSkeleton>
                </Flex>
                <Flex gap={{ base: 8, md: 16 }} direction="column">
                    {data?.length || isLoading ? (
                        <Flex
                            direction={{ base: 'column', md: 'row' }}
                            gap={{ base: 8 }}
                            justifyContent="center"
                            data-testid="gift-cards-layout-cards"
                        >
                            {isLoading ? (
                                <>
                                    <GiftCardsCardContainerSkeleton />
                                    <GiftCardsCardContainerSkeleton />
                                </>
                            ) : (
                                data?.map((product) => (
                                    <PdpProvider
                                        key={product.id}
                                        productId={product?.variants?.[0].productId || ''}
                                    >
                                        <GiftCard />
                                    </PdpProvider>
                                ))
                            )}
                        </Flex>
                    ) : null}
                    <GiftCardsYouGiveCardSkeleton isLoaded={!isLoading}>
                        <GiftCardsYouGiveSection onCheckBalanceClick={onOpen} />
                    </GiftCardsYouGiveCardSkeleton>
                    <GiftCardsFAQCardSkeleton isLoaded={!isLoading}>
                        <GiftCardsFAQSection onCheckBalanceClick={onOpen} />
                    </GiftCardsFAQCardSkeleton>
                </Flex>
            </Flex>
        </Layout>
    );
};

const GiftCards: PageFC = () => (
    <ErrorBoundary FallbackComponent={EnhancedError}>
        <GiftCardsLayout />
    </ErrorBoundary>
);

export { GiftCards };
